import type { ArtifactDesign, ArtifactCollection, Artifact } from '@/types/artifactDesign';

const ARTIFACT_KEYS = ['skills', 'subskills', 'subflows', 'attributes', 'integrations', 'facets'] as const;
type ArtifactKey = (typeof ARTIFACT_KEYS)[number];

const DEFAULT_CONNECTOR = {
  protocol: 'REST' as const,
  integrationMethod: 'API' as const,
  connectorGuid: 'a2fddace-c7b6-404a-835d-bd5c3a380beb',
  connectorId: -5,
  operationIdentifier: 'invokeRestApi',
  operationId: -9
};

type RawCollection<T extends Artifact = Artifact> =
  | ArtifactCollection<T>
  | { type?: T[]; items?: T[] }
  | T[]
  | undefined
  | null;

const ensureSuffix = (value: string, suffix: string) => {
  if (!suffix) return value;
  const normalizedSuffix = suffix.startsWith('_') ? suffix : `_${suffix}`;
  return value.endsWith(normalizedSuffix) ? value : `${value}${normalizedSuffix}`;
};

const stripSuffix = (value: string, suffix: string) => {
  if (!suffix) return value;
  const normalizedSuffix = suffix.startsWith('_') ? suffix : `_${suffix}`;
  return value.endsWith(normalizedSuffix) ? value.slice(0, -normalizedSuffix.length) : value;
};

const toKebabCase = (value: string) => value.replace(/_/g, '-').toLowerCase();

const normalizeItems = <T extends Artifact = Artifact>(collection: RawCollection<T>): T[] => {
  if (!collection) return [];

  if (Array.isArray(collection)) {
    return collection;
  }

  if ('items' in collection && Array.isArray(collection.items)) {
    return collection.items;
  }

  if ('type' in collection && Array.isArray(collection.type)) {
    return collection.type;
  }

  return [];
};

const withIdentifierSuffix = <T extends Artifact>(items: T[], suffix: string): T[] => {
  if (!suffix) return items;

  return items.map((item) => {
    if (item && typeof item.identifier === 'string') {
      return {
        ...item,
        identifier: ensureSuffix(item.identifier, suffix)
      } as T;
    }
    return item;
  });
};

/**
 * Normalizes LLM generated artifact design payloads so downstream UI logic can
 * rely on a consistent schema. The LLM occasionally introduces the following
 * variations:
 *
 * - Places artifact collections (e.g. integrations) at the top-level instead
 *   of under `artifacts`
 * - Wraps collections in `{ type: [...items] }` or `{ items: [...] }`
 * - Omits previously suffixed identifiers
 *
 * This helper consolidates those cases, enforces idempotent suffixing, and
 * guarantees each collection uses the `{ items: [] }` structure expected by the UI.
 */
export const normalizeArtifactDesign = (
  rawDesign: any,
  projectSuffix: string
): ArtifactDesign => {
  const clone = JSON.parse(JSON.stringify(rawDesign ?? {}));
  const normalized: any = { ...clone };

  normalized.version = '1.0';
  if (projectSuffix) {
    normalized.projectSuffix = projectSuffix.replace(/^_/, '');
  } else if (!normalized.projectSuffix) {
    normalized.projectSuffix = '';
  }

  if (!Array.isArray(normalized.relationships)) {
    normalized.relationships = [];
  }

  const suffix = normalized.projectSuffix || projectSuffix || '';
  const artifacts: Record<ArtifactKey, ArtifactCollection<Artifact>> = {} as Record<ArtifactKey, ArtifactCollection<Artifact>>;

  ARTIFACT_KEYS.forEach((key: ArtifactKey) => {
    const rawCollection = clone?.artifacts?.[key] ?? clone?.[key as keyof typeof clone];
    const items = withIdentifierSuffix(normalizeItems(rawCollection), suffix).map((item) => {
      // Default to selected=true if undefined (LLM doesn't set this)
      const itemWithSelection = { ...item, selected: item.selected ?? true };

      if (key === 'integrations') {
        const integration: any = { ...itemWithSelection };
        integration.integrationType = integration.integrationType || 'api';
        integration.connector = { ...DEFAULT_CONNECTOR, ...(integration.connector || {}) };

        const baseIdentifier = stripSuffix(integration.identifier || '', suffix) || toKebabCase(integration.name || 'integration');
        const kebab = toKebabCase(baseIdentifier || 'integration');

        if (!Array.isArray(integration.operations) || integration.operations.length === 0) {
          integration.operations = [
            {
              name: `Invoke ${integration.name || 'Integration'}`,
              description: `Automatically generated operation for ${integration.name || integration.identifier}`,
              httpMethod: 'POST',
              requestUri: `/${kebab || 'operation'}`,
              headers: {
                'Content-Type': 'application/json'
              },
              timeout: 30000,
              outboundRequestMapping: {},
              inboundResponseMapping: []
            }
          ];
        }

        if (!Array.isArray(integration.instances) || integration.instances.length === 0) {
          integration.instances = [
            {
              name: `${integration.name || 'Primary'} Instance`,
              description: `Auto-generated instance for ${integration.name || integration.identifier}`,
              url: `https://api.${kebab || 'integration'}.example.com`,
              authenticationType: 'Bearer',
              globalHeaders: {
                Authorization: 'Bearer @{local.api_token}'
              },
              defaultInstance: true
            }
          ];
        }

        return integration;
      }

      return itemWithSelection;
    });

    artifacts[key] = { items } as ArtifactCollection<Artifact>;
    if (normalized[key as keyof typeof normalized]) {
      delete normalized[key as keyof typeof normalized];
    }
  });

  normalized.artifacts = artifacts;

  return normalized as ArtifactDesign;
};
